import { GameMode, MatchHistoryRecord, OverallPlayerStats } from '../types/darts';
import { Match, parseMatch } from '../engine/match';
import { ensureProfiles, findDuplicateName, getProfiles } from './players';

const SCHEMA_KEY = 'darts_pwa_schema';
const SCHEMA_VERSION = 2;
const HISTORY_KEY = 'darts_pwa_match_history';
const ACTIVE_GAME_KEY = 'darts_pwa_active_game'; // oude opslag (vóór de spelmotor), wordt opgeruimd
const ACTIVE_MATCH_KEY = 'darts_pwa_active_match';
const PREFS_KEY = 'darts_pwa_preferences';

export const THEME_COLORS = ['emerald', 'dartboard', 'amber'] as const;
export type ThemeColor = (typeof THEME_COLORS)[number];
export type ThemeMode = 'light' | 'dark' | 'auto';

export interface UserPreferences {
  themeMode: ThemeMode;
  darkMode?: boolean; // legacy compatibility
  soundEnabled: boolean;
  language: 'nl' | 'en';
  defaultInputMethod: 'per-dart' | 'turn-total';
  defaultGameMode: '901' | '501' | '301' | 'cricket';
  themeColor: ThemeColor;
}

export const DEFAULT_PREFS: UserPreferences = {
  themeMode: 'auto',
  soundEnabled: true,
  language: 'nl',
  defaultInputMethod: 'per-dart',
  defaultGameMode: '501',
  themeColor: 'emerald'
};

/** Leest een datum als "09-10-2026 08:05" (oude records) terug als tijdstip. */
export function parseNlDate(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const m = value.match(/(\d{1,2})-(\d{1,2})-(\d{4})(?:\D+(\d{1,2}):(\d{2}))?/);
  if (!m) return null;
  const [, d, mo, y, h = '0', mi = '0'] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  return isNaN(date.getTime()) ? null : date.getTime();
}

/**
 * Zet opgeslagen gegevens eenmalig om naar de huidige vorm:
 * spelers in de geschiedenis krijgen een vaste profiel-ID en elk record een tijdstip.
 */
export function migrateStorage(): void {
  try {
    const version = Number(localStorage.getItem(SCHEMA_KEY) || 1);
    if (version >= SCHEMA_VERSION) return;

    const history = getMatchHistory().map(record => {
      const names = record.players.map(p => p.name || 'Speler');
      const profiles = ensureProfiles(names);
      return {
        ...record,
        playedAt: record.playedAt ?? parseNlDate(record.date) ?? 0,
        players: record.players.map((p, i) => ({ ...p, id: profiles[i].id }))
      };
    });
    history.sort((a, b) => (b.playedAt ?? 0) - (a.playedAt ?? 0));
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));

    // Een lopende wedstrijd houdt zijn spelers, maar krijgt dezelfde profiel-ID's.
    const raw = localStorage.getItem(ACTIVE_MATCH_KEY);
    const active = raw ? parseMatch(JSON.parse(raw)) : null;
    if (active && !findDuplicateName(active.players.map(p => p.name))) {
      const profiles = ensureProfiles(active.players.map(p => p.name));
      const players = active.players.map((p, i) => ({ ...p, id: profiles[i].id }));
      localStorage.setItem(ACTIVE_MATCH_KEY, JSON.stringify({ ...active, players }));
    }

    localStorage.setItem(SCHEMA_KEY, String(SCHEMA_VERSION));
  } catch (e) {
    console.error('Failed to migrate storage', e);
  }
}

// Match History Storage
export function getMatchHistory(): MatchHistoryRecord[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(r => r && typeof r.id === 'string' && Array.isArray(r.players)) : [];
  } catch (e) {
    console.error('Failed to load match history', e);
    return [];
  }
}

export function saveMatchRecord(record: MatchHistoryRecord): void {
  try {
    // Zelfde wedstrijd opnieuw opslaan (bv. na herstellen en opnieuw uitgooien) vervangt het oude record.
    const history = getMatchHistory().filter(item => item.id !== record.id);
    history.unshift(record); // newest first
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 100))); // keep last 100
  } catch (e) {
    console.error('Failed to save match record', e);
  }
}

/** Zet de hele geschiedenis terug (bv. bij "Ongedaan maken" na verwijderen). */
export function replaceMatchHistory(records: MatchHistoryRecord[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(records.slice(0, 100)));
  } catch (e) {
    console.error('Failed to restore match history', e);
  }
}

export function clearMatchHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch (e) {
    console.error('Failed to clear match history', e);
  }
}

export function hasMatchRecord(id: string): boolean {
  return getMatchHistory().some(item => item.id === id);
}

export function deleteMatchRecord(id: string): void {
  try {
    const history = getMatchHistory();
    const updated = history.filter(item => item.id !== id);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to delete match record', e);
  }
}

// Active Match Storage
export function getActiveMatch(): Match | null {
  try {
    localStorage.removeItem(ACTIVE_GAME_KEY);
    const raw = localStorage.getItem(ACTIVE_MATCH_KEY);
    return raw ? parseMatch(JSON.parse(raw)) : null;
  } catch (e) {
    return null;
  }
}

export function saveActiveMatch(match: Match | null): void {
  try {
    if (!match) {
      localStorage.removeItem(ACTIVE_MATCH_KEY);
    } else {
      localStorage.setItem(ACTIVE_MATCH_KEY, JSON.stringify(match));
    }
  } catch (e) {
    console.error('Failed to save active match', e);
  }
}

// User Preferences Storage
export function getPreferences(): UserPreferences {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_PREFS;
    let themeMode: ThemeMode = parsed.themeMode;
    if (!['light', 'dark', 'auto'].includes(themeMode)) {
      if (typeof parsed.darkMode === 'boolean') {
        themeMode = parsed.darkMode ? 'dark' : 'light';
      } else {
        themeMode = 'auto';
      }
    }
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      themeMode,
      // Onbekende waarden (bv. het verdwenen thema 'cyber') vallen terug op de standaard.
      themeColor: (THEME_COLORS as readonly string[]).includes(parsed.themeColor) ? parsed.themeColor : DEFAULT_PREFS.themeColor,
      language: parsed.language === 'en' ? 'en' : 'nl',
      soundEnabled: typeof parsed.soundEnabled === 'boolean' ? parsed.soundEnabled : DEFAULT_PREFS.soundEnabled
    };
  } catch (e) {
    return DEFAULT_PREFS;
  }
}

export function savePreferences(prefs: Partial<UserPreferences>): UserPreferences {
  try {
    const current = getPreferences();
    const updated = { ...current, ...prefs };
    localStorage.setItem(PREFS_KEY, JSON.stringify(updated));
    return updated;
  } catch (e) {
    return DEFAULT_PREFS;
  }
}

// Compute Aggregated Statistics per player across all match history records
export function getOverallStats(gameModeFilter?: GameMode | 'all'): OverallPlayerStats[] {
  try {
    const history = getMatchHistory();
    const map: Record<string, OverallPlayerStats> = {};
    // Huidige namen per profiel: na een naamswijziging toont de ranglijst de nieuwe naam.
    const currentNames = new Map(getProfiles().map(p => [p.id, p.name]));

    history.forEach(match => {
      if (gameModeFilter && gameModeFilter !== 'all' && match.gameMode !== gameModeFilter) {
        return;
      }
      if (!match.players || !Array.isArray(match.players)) return;

      match.players.forEach(p => {
        if (!p || !p.name) return;
        const key = p.id || p.name.trim().toLowerCase();
        if (!map[key]) {
          map[key] = {
            playerId: key,
            playerName: currentNames.get(key) ?? p.name.trim(),
            matchesPlayed: 0,
            matchesWon: 0,
            legsWonTotal: 0,
            totalDartsThrown: 0,
            totalPointsScored: 0,
            overallAvg3Dart: 0,
            first9Avg: 0,
            first9Points: 0,
            first9Darts: 0,
            highestCheckout: 0,
            highestTurn: 0,
            checkoutAttempts: 0,
            checkoutHits: 0,
            checkoutPercent: 0,
            total180s: 0,
            total140Plus: 0,
            total100Plus: 0
          };
        }

        const st = map[key];
        st.matchesPlayed += 1;
        if (p.isWinner) st.matchesWon += 1;
        st.legsWonTotal += p.legsWon || 0;
        st.highestTurn = Math.max(st.highestTurn, p.highestTurn || 0);
        st.highestCheckout = Math.max(st.highestCheckout, p.highestCheckout || 0);
        st.total180s += p.count180s || 0;
        st.total140Plus += p.count140Plus || 0;
        st.total100Plus += p.count100Plus || 0;
        st.first9Points += p.first9Points || 0;
        st.first9Darts += p.first9Darts || 0;
        st.checkoutAttempts = (st.checkoutAttempts || 0) + (p.checkoutAttempts || 0);
        st.checkoutHits = (st.checkoutHits || 0) + (p.checkoutHits || 0);

        if (typeof p.dartsThrown === 'number' && typeof p.pointsScored === 'number') {
          st.totalDartsThrown += p.dartsThrown;
          st.totalPointsScored += p.pointsScored;
        } else if (p.avg3Dart && p.avg3Dart > 0) {
          // Oude records zonder darts/punten: tellen mee als 30 darts tegen hun gemiddelde.
          st.totalPointsScored += p.avg3Dart * 10;
          st.totalDartsThrown += 30;
        }
      });
    });

    return Object.values(map).map(st => {
      const attempts = st.checkoutAttempts || 0;
      const hits = st.checkoutHits || 0;
      const checkoutPercent = attempts > 0 ? Math.min(100, Math.round((hits / attempts) * 100)) : 0;

      return {
        ...st,
        checkoutPercent,
        first9Avg: st.first9Darts > 0 ? Math.round((st.first9Points / st.first9Darts) * 3 * 10) / 10 : 0,
        overallAvg3Dart: st.totalDartsThrown > 0 
          ? Math.round((st.totalPointsScored / st.totalDartsThrown) * 3 * 10) / 10 
          : 0
      };
    }).sort((a, b) => b.matchesWon - a.matchesWon || b.overallAvg3Dart - a.overallAvg3Dart);
  } catch (e) {
    console.error('Failed to compute overall stats', e);
    return [];
  }
}

